import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import AuthPageShell from './AuthPageShell';
import GoogleSignInButton from './shared/GoogleSignInButton';
import { getPublicAuthOrigin, isOAuthAuthHost, isProductionMultiHostAuth } from '../utils/authOrigin';
import './Login.css';

/**
 * Apex-only: receives a handoff from the district subdomain, links Google, then hands off back to the subdomain.
 */
const GoogleLinkBridge = () => {
  const [searchParams] = useSearchParams();
  const { setAuth, linkGoogle } = useAuth();
  const [error, setError] = useState('');
  const [phase, setPhase] = useState('loading'); // loading | link | error

  useEffect(() => {
    if (!isProductionMultiHostAuth()) {
      setError('Google account linking from this page is only used on the live site (district subdomains). On localhost, link Google from the district dashboard directly.');
      setPhase('error');
      return;
    }
    if (!isOAuthAuthHost()) {
      setError('Open this page from the link on your district site (secure Google sign-in uses the main Back to School Forms domain).');
      setPhase('error');
      return;
    }

    const session = searchParams.get('session');
    if (!session) {
      setError('Missing session. Go back to your district dashboard and choose Link Google again.');
      setPhase('error');
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.post('/api/auth/handoff-session/redeem', { code: session });
        const token = data?.token;
        const user = data?.user;
        if (!token || !user?.role) {
          if (!cancelled) {
            setError('Invalid server response.');
            setPhase('error');
          }
          return;
        }
        setAuth(token, user);
        if (!cancelled) setPhase('link');
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.error || e.message || 'Session expired. Try again from your district site.');
          setPhase('error');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams, setAuth]);

  const handleCredential = async (credential) => {
    setError('');
    const lr = await linkGoogle(credential);
    if (!lr.success) {
      setError(lr.error);
      return;
    }
    try {
      const me = (await axios.get('/api/auth/me')).data;
      const sub = me?.subdomain;
      if (!sub) {
        setError('Your account has no district subdomain. Contact support.');
        return;
      }
      const { data } = await axios.post('/api/auth/handoff-session');
      const code = data?.code;
      if (!code) {
        setError('Could not create return session. Try logging in again.');
        return;
      }
      const target = `https://${sub}.backtoschoolforms.com/auth/import?code=${encodeURIComponent(code)}&next=${encodeURIComponent('/district-admin')}`;
      window.location.href = target;
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Something went wrong');
    }
  };

  if (phase === 'error') {
    return (
      <AuthPageShell>
        <div className="login-box">
          <h1>Link Google</h1>
          <div className="error-message">{error}</div>
          <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '1rem' }}>
            Use your district site URL, or{' '}
            <Link to="/login">sign in on {getPublicAuthOrigin().replace(/^https?:\/\//, '')}</Link>.
          </p>
        </div>
      </AuthPageShell>
    );
  }

  if (phase === 'loading') {
    return (
      <AuthPageShell>
        <div className="login-box">
          <p style={{ textAlign: 'center', color: '#666' }}>Preparing Google sign-in…</p>
        </div>
      </AuthPageShell>
    );
  }

  return (
    <AuthPageShell>
      <div className="login-box">
        <h1>Link Google</h1>
        <p style={{ color: '#666', fontSize: '0.95rem', marginBottom: '1rem', textAlign: 'center' }}>
          Sign in with Google to link the account that matches your district email.
        </p>
        {error ? <div className="error-message">{error}</div> : null}
        <GoogleSignInButton text="signin_with" onCredential={handleCredential} />
      </div>
    </AuthPageShell>
  );
};

export default GoogleLinkBridge;
